import type { BaseClientMessage } from "@frontierx/shared/schemas/clientMessageTypes";
import { createTypeSafeSendClientEvent } from "@frontierx/shared/schemas/clientMessageTypes";
import type { PlaybookScriptItem } from "@frontierx/types";
import type React from "react";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CompatibleSocket } from "~/app/types/CompatibleSocket";

import { useWebSocketEventLogger } from "../../hooks/useWebSocketEventLogger";
// Import individual selectors instead of the monolithic hook
import {
  type BotSocketType,
  useAIHistory,
  useCallStore,
  useClearError,
  useCurrentAiHelpRequest,
  useCurrentScriptItem,
  useError,
  useIsStreaming,
  useLatencyMetrics,
  useMessageLatency,
  useParticipantRoles,
  useParticipants,
  usePartyServerConnectionStatus,
  usePlaybook,
  useQuestions,
  useReconnectAttempt,
  useRoomBot,
  useScriptDetectionLogs,
  useStreamingResponse,
  useStreamingStatus,
  useTranscript,
} from "../../stores/callStore";
import { useAuth } from "../Auth/UnifiedAuthProvider";
import type { WebSocketEvent } from "../Debug/WebSocketEventLogger";
import type { HandleGetHelpFunctionArgs } from "../Help/AiHelpCard";

export type CallContextType = {
  // Call management
  leaveCall: () => void;
  roomId: string;
  mainSocket: CompatibleSocket;
  activeMode: CallDisplayMode;
  setActiveMode: React.Dispatch<React.SetStateAction<CallDisplayMode>>;
  handleGetAIHelp: (args: HandleGetHelpFunctionArgs) => void;

  // Bot socket (TODO: This should probably be managed in Zustand too)
  botSocket: BotSocketType;

  // Handler functions (now with real implementations!)
  handleGetHelp: (args: HandleGetHelpFunctionArgs) => void;
  handleRequestBotInfo: () => void;
  handleRequestSummary: () => void;
  handleCompleteScriptItem: (scriptItem?: PlaybookScriptItem) => void;
  handleSetCurrentScriptItem: (scriptItem?: PlaybookScriptItem) => void;

  // State from Zustand store (accessed via selectors for optimal re-renders)
  transcript: ReturnType<typeof useTranscript>;
  roomBot: ReturnType<typeof useRoomBot>;
  aiHistory: ReturnType<typeof useAIHistory>;
  error: ReturnType<typeof useError>;
  clearError: ReturnType<typeof useClearError>;
  streamingResponse: ReturnType<typeof useStreamingResponse>;
  isStreaming: ReturnType<typeof useIsStreaming>;
  streamingStatus: ReturnType<typeof useStreamingStatus>;
  latencyMetrics: ReturnType<typeof useLatencyMetrics>;
  messageLatency: ReturnType<typeof useMessageLatency>;
  playbook: ReturnType<typeof usePlaybook>;
  participants: ReturnType<typeof useParticipants>;
  currentScriptItem: ReturnType<typeof useCurrentScriptItem>;
  partyServerConnectionStatus: ReturnType<
    typeof usePartyServerConnectionStatus
  >;
  questions: ReturnType<typeof useQuestions>;
  reconnectAttempt: ReturnType<typeof useReconnectAttempt>;
  currentAiHelpRequest: ReturnType<typeof useCurrentAiHelpRequest>;
  participantRoles: ReturnType<typeof useParticipantRoles>;
  scriptDetectionLogs: ReturnType<typeof useScriptDetectionLogs>;

  // Bot status polling

  // WebSocket event logging
  webSocketEvents: WebSocketEvent[];
  clearWebSocketEvents: () => void;

  // Computed values
  isThrashing: boolean;
  shouldSuggestRefresh: boolean;
  thrashingMessage: string;
};

export type CallDisplayMode =
  | "ai"
  | "transcript"
  | "script"
  | "debug"
  | "report";

const CallContext = createContext<CallContextType | undefined>(undefined);

export interface CallProviderProps {
  children: ReactNode;
  roomId: string;
  leaveCall: () => void;
  mainSocket: CompatibleSocket;
  // Temporary: pass botSocket until it's fully migrated to Zustand
  botSocket: BotSocketType;
}

export function CallProvider({
  children,
  roomId,
  leaveCall,
  mainSocket,
  botSocket,
}: Readonly<CallProviderProps>) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  // Use individual Zustand selectors for optimal re-renders
  const transcript = useTranscript();
  const roomBot = useRoomBot();
  const aiHistory = useAIHistory();
  const error = useError();
  const clearError = useClearError();
  const streamingResponse = useStreamingResponse();
  const isStreaming = useIsStreaming();
  const streamingStatus = useStreamingStatus();
  const latencyMetrics = useLatencyMetrics();
  const messageLatency = useMessageLatency();
  const playbook = usePlaybook();
  const participants = useParticipants();
  const currentScriptItem = useCurrentScriptItem();
  const partyServerConnectionStatus = usePartyServerConnectionStatus();
  const questions = useQuestions();
  const reconnectAttempt = useReconnectAttempt();
  const currentAiHelpRequest = useCurrentAiHelpRequest();
  const participantRoles = useParticipantRoles();
  const scriptDetectionLogs = useScriptDetectionLogs();

  // WebSocket event logging
  const {
    events: webSocketEvents,
    clearEvents: clearWebSocketEvents,
    logSentMessage,
    logError,
  } = useWebSocketEventLogger();

  // Get Zustand store actions
  const setCurrentAiHelpRequest = useCallStore(
    (state) => state.setCurrentAiHelpRequest,
  );
  const setIsStreaming = useCallStore((state) => state.setIsStreaming);
  const setStreamingResponse = useCallStore(
    (state) => state.setStreamingResponse,
  );
  const setError = useCallStore((state) => state.setError);

  const [activeMode, setActiveMode] = useState<CallDisplayMode>("script");

  // Streaming timeout ref and function (lifted from useCallDisplay)
  const streamingTimeoutRef = useRef<number | null>(null);
  const resetStreamingTimeout = useCallback(() => {
    if (isStreaming) {
      if (streamingTimeoutRef.current !== null) {
        window.clearTimeout(streamingTimeoutRef.current);
      }
      streamingTimeoutRef.current = window.setTimeout(() => {
        if (isStreaming) {
          setIsStreaming(false);
          setCurrentAiHelpRequest(undefined);
          setStreamingResponse(null);
        }
      }, 20000); // 20 seconds timeout
    }
  }, [
    isStreaming,
    setIsStreaming,
    setCurrentAiHelpRequest,
    setStreamingResponse,
  ]);

  // Send client event function with strong typing and validation
  const sendClientEvent = createTypeSafeSendClientEvent(
    (data: string) => {
      // Log the sent message for debugging
      try {
        const parsedMessage = JSON.parse(data);
        logSentMessage(parsedMessage.type, parsedMessage);
      } catch (error) {
        logError(
          "sent",
          "PARSE_ERROR",
          data,
          `Failed to parse sent message: ${error}`,
        );
      }
      return botSocket.send(data);
    },
    {
      logMessages: false, // Only log important events (handled internally)
      onError: (error, message) => {
        setError(`Failed to send ${message.type}: ${error.message}`);
      },
    },
  );

  // Handler implementations (lifted from useCallDisplay)
  const handleGetHelp = useCallback(
    (args: HandleGetHelpFunctionArgs) => {
      const { question, transcriptItemIds, detectedQuestion } = args;

      if (!orgId) {
        console.error("No orgId found");
        return;
      }
      const message: BaseClientMessage = {
        type: "GET_HELP",
        payload: {
          orgId,
          question,
          transcriptItemIds,
          detectedQuestion,
        },
      };

      setCurrentAiHelpRequest(args);
      sendClientEvent(message);
      setIsStreaming(true);
      setStreamingResponse(null);
      setError(null);
      resetStreamingTimeout();
    },
    [
      resetStreamingTimeout,
      sendClientEvent,
      orgId,
      setCurrentAiHelpRequest,
      setIsStreaming,
      setStreamingResponse,
      setError,
    ],
  );

  const handleRequestBotInfo = useCallback(() => {
    sendClientEvent({ type: "REQUEST_BOT_INFO" });
  }, [sendClientEvent]);

  const handleRequestSummary = useCallback(() => {
    sendClientEvent({ type: "REQUEST_SUMMARY" });
  }, [sendClientEvent]);

  const handleCompleteScriptItem = useCallback(
    (scriptItem?: PlaybookScriptItem) => {
      if (!scriptItem) {
        return;
      }
      if (scriptItem.completed) {
        sendClientEvent({
          type: "UNCOMPLETE_SCRIPT_ITEM",
          payload: scriptItem,
        });
      } else {
        sendClientEvent({
          type: "COMPLETE_SCRIPT_ITEM",
          payload: scriptItem,
        });
      }
    },
    [sendClientEvent],
  );

  const handleSetCurrentScriptItem = useCallback(
    (scriptItem?: PlaybookScriptItem) => {
      if (!scriptItem) {
        return;
      }
      sendClientEvent({
        type: "SET_CURRENT_SCRIPT_ITEM",
        payload: scriptItem,
      });
    },
    [sendClientEvent],
  );

  const handleGetAIHelp = useCallback(
    (args: HandleGetHelpFunctionArgs) => {
      console.log("Getting AI help");
      setActiveMode("ai");
      handleGetHelp(args);
    },
    [handleGetHelp],
  );

  // Computed values based on reconnect attempts
  const isThrashing = reconnectAttempt >= 3;
  const shouldSuggestRefresh = reconnectAttempt >= 5;
  const thrashingMessage = shouldSuggestRefresh
    ? "Connection issues - consider refreshing the page"
    : "";

  const value = useMemo(
    () => ({
      // Call management
      leaveCall,
      roomId,
      mainSocket,
      activeMode,
      setActiveMode,
      handleGetAIHelp,

      // Bot socket (temporary until migrated)
      botSocket,

      // Handler functions (now with real implementations!)
      handleGetHelp,
      handleRequestBotInfo,
      handleRequestSummary,
      handleCompleteScriptItem,
      handleSetCurrentScriptItem,

      // State from individual selectors (no object spread needed!)
      transcript,
      roomBot,
      aiHistory,
      error,
      clearError,
      streamingResponse,
      isStreaming,
      streamingStatus,
      latencyMetrics,
      messageLatency,
      playbook,
      participants,
      currentScriptItem,
      partyServerConnectionStatus,
      questions,
      reconnectAttempt,
      currentAiHelpRequest,
      participantRoles,
      scriptDetectionLogs,

      // WebSocket event logging
      webSocketEvents,
      clearWebSocketEvents,

      // Computed values
      isThrashing,
      shouldSuggestRefresh,
      thrashingMessage,
    }),
    [
      // Only depend on the values we control directly and the selector results
      leaveCall,
      roomId,
      mainSocket,
      activeMode,
      handleGetAIHelp,

      // Bot socket and handlers
      botSocket,
      handleGetHelp,
      handleRequestBotInfo,
      handleRequestSummary,
      handleCompleteScriptItem,
      handleSetCurrentScriptItem,

      // Individual selector results (each selector is optimized to prevent unnecessary re-renders)
      transcript,
      roomBot,
      aiHistory,
      error,
      clearError,
      streamingResponse,
      isStreaming,
      streamingStatus,
      latencyMetrics,
      messageLatency,
      playbook,
      participants,
      currentScriptItem,
      partyServerConnectionStatus,
      questions,
      reconnectAttempt,
      currentAiHelpRequest,
      participantRoles,
      scriptDetectionLogs,

      // Bot status polling

      // WebSocket event logging
      webSocketEvents,
      clearWebSocketEvents,

      // Computed values
      isThrashing,
      shouldSuggestRefresh,
      thrashingMessage,
    ],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCallContext() {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error("useCallContext must be used within a CallProvider");
  }
  return context;
}
